import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody, Chip, Typography, Alert,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileUploadField } from '../../../components/FileUploadField';
import { dealersApi } from '../../../api/dealers.api';
import { extractApiError } from '../../../api/error';
import { useAuth } from '../../../hooks/useAuth';

interface Props {
  dealerId: number | null;
  dealerName?: string;
  onClose: () => void;
}

const DOC_TYPES = [
  { value: 'gst_certificate', label: 'GST Certificate' },
  { value: 'pan_card', label: 'PAN Card' },
  { value: 'cancelled_cheque', label: 'Cancelled Cheque' },
  { value: 'dealership_agreement', label: 'Dealership Agreement' },
  { value: 'address_proof', label: 'Address Proof' },
  { value: 'other', label: 'Other' },
];

export function DealerDocumentsDialog({ dealerId, dealerName, onClose }: Props) {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const open = dealerId !== null;
  const [docType, setDocType] = useState('gst_certificate');
  const [fileName, setFileName] = useState('');
  const [fileUrl, setFileUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: docs = [] } = useQuery({
    queryKey: ['dealer-docs', dealerId],
    queryFn: () => dealersApi.listDocuments(dealerId as number),
    enabled: open,
  });

  const addMut = useMutation({
    mutationFn: () => dealersApi.addDocument(dealerId as number, { docType, fileName, fileUrl }),
    onSuccess: () => {
      setFileName(''); setFileUrl(''); setError(null);
      qc.invalidateQueries({ queryKey: ['dealer-docs', dealerId] });
    },
    onError: (e) => setError(extractApiError(e)),
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Documents — {dealerName}</DialogTitle>
      <DialogContent>
        {hasPermission('dealer_document.create') && (
          <>
            <Typography variant="subtitle2" gutterBottom>Add document (new version)</Typography>
            {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
            <Stack direction="row" spacing={2} mb={2}>
              <TextField select size="small" label="Type" value={docType} onChange={(e) => setDocType(e.target.value)} sx={{ minWidth: 200 }}>
                {DOC_TYPES.map((d) => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
              </TextField>
              <TextField size="small" label="File name" value={fileName} onChange={(e) => setFileName(e.target.value)} />
              <FileUploadField label="Document" value={fileUrl || null} onChange={(url) => setFileUrl(url ?? '')} />
              <Button variant="contained" disabled={!fileName || !fileUrl} onClick={() => addMut.mutate()}>Add</Button>
            </Stack>
          </>
        )}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell><TableCell>File</TableCell><TableCell>Version</TableCell><TableCell>Current</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {docs.map((d) => (
              <TableRow key={d.id}>
                <TableCell>{d.docType}</TableCell>
                <TableCell><a href={d.fileUrl} target="_blank" rel="noreferrer">{d.fileName}</a></TableCell>
                <TableCell>v{d.version}</TableCell>
                <TableCell>{d.isCurrent ? <Chip size="small" color="success" label="current" /> : '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
